import { act, useCallback, useState } from 'react';
import { Button, useMediaQuery, Checkbox, styled } from '@mui/material';
import config from '../config.json';
import {
  deaNumUsersAllSelected,
  disableOperationsHelpDesk,
  IDeaNumUser,
  IUser,
  searchEmailsMinLength,
  StyledAutocomplete2,
  SuccessIcon,
  toggleItem,
  useRoleOpContext,
  ValueLabel,
} from '../common';
import {
  IRequestApiCallStatus,
  StatusSaveRequst,
  TeamRequestAction,
  UserRoles2d,
  UserRolesStack,
} from '../team-request';
import { Justification, justificationOptions } from '../request';
import {
  excludeUserRoles,
  saveRequest,
  useConfirmSubmitRequest,
  useEmailSearch,
  useSerialNumberSearch,
  useUserRoleSearch,
} from '.';
import {
  noop,
  compose as lcompose,
  tap as ltap,
  trim as ltrim,
  filter as lfilter,
  map as lmap,
  pick as lpick,
  isEmpty,
} from 'lodash/fp';
import { always, pathOr } from 'rambda';
import { left, right, map as emap } from 'fp-ts/lib/Either';
import { map } from 'fp-ts/Array';
import { flow, pipe } from 'fp-ts/lib/function';

const properties: ValueLabel<keyof IDeaNumUser>[] = [
  { value: 'deaNumber', label: 'DEA Registration Number' },
  { value: 'role', label: 'Role' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'loginEmail', label: 'Email' },
];

export const Request = ({ user, raTab, diTab }: { user: IUser; raTab?: boolean; diTab?: boolean; }) => {
  const matchesGridBreakpoint = useMediaQuery('(min-width:60em)', {
    noSsr: true,
  });

  const { emails, emailInput, setEmailInput } = useEmailSearch({
    forRegistrantsNCoordinators: false,
  });
  const { serialNumbers, serialNumberInput, setSerialNumberInput } =
    useSerialNumberSearch({ forRegistrantsNCoordinators: false });

  const [email, setEmail] = useState<string | null>(null);
  const [serialNumber, setSerialNumber] = useState<string | null>(null);
  const [deaNumber, setDeaNumber] = useState<string | null>(null);

  const activeEmailOptions = emails.filter(e => e.active).map(e => e.email);
  const activeSerialNumberOptions = serialNumbers.filter(s => s.active).map(s => s.serialNumber);

  const { userRoles } = useUserRoleSearch({
    forRegistrantsNCoordinators: false,
    email,
    serialNumber,
    deaNumber,
  });

  const filteredUserRoles = (raTab || diTab)
    ? userRoles.filter(u => u.active !== 0)
    : userRoles;

  const [selectedUserRoleKeys, setSelectedUserRoleKeys] = useState<string[]>(
    []
  );
  const toggleUserRole = ({ type, payload }: TeamRequestAction) =>
    type === 'ToggleUserRole'
      ? payload === 'all'
        ? toggleUserRoleAll()
        : pipe(
          payload,
          toggleItem(selectedUserRoleKeys),
          setSelectedUserRoleKeys
        )
      : void 0;

  const toggleUserRoleAll = () =>
    pipe(
      deaNumUsersAllSelected({
        deaNumUsers: excludeUserRoles(revokedUserRoleKeys)(filteredUserRoles),
        selectedUserRoleKeys,
      }),
      allSelected =>
        allSelected
          ? setSelectedUserRoleKeys([])
          : setSelectedUserRoleKeys(
            pipe(
              excludeUserRoles(revokedUserRoleKeys)(filteredUserRoles),
              map<IDeaNumUser, string>(pathOr('', 'key'))
            )
          )
    );

  const [justification, setJustification] = useState('');
  const [otherComments, setOtherComments] = useState('');

  const [apiCallStatus, setApiCallStatus] = useState<IRequestApiCallStatus>({
    type: 'ConfirmSubmitRequest',
    apiCallStatus: void 0,
  });

  const dispatchSaveRequest = useCallback(
    ({ type, payload }: TeamRequestAction) =>
      type === 'SaveRequestSuccess'
        ? flow(
          () =>
            setApiCallStatus({
              type: 'SaveRequest',
              apiCallStatus: true,
            }),
          () => setRevokedUserRoleKeys(a => [...a, ...selectedUserRoleKeys]),
          () => setSelectedUserRoleKeys([])
        )()
        : setApiCallStatus({
          type: 'SaveRequest',
          apiCallStatus: false,
        }),
    [selectedUserRoleKeys]
  );

  const dispatchConfirmSubmitRequest = useCallback(
    ({ type, payload }: TeamRequestAction) =>
      type === 'SaveRequest'
        ? flow(
          () =>
            setApiCallStatus(a => ({
              ...a,
              type: 'SaveRequest',
              apiCallStatus: null,
            })),
          () =>
            saveRequest(dispatchSaveRequest)(user)({
              requestorComments:
                justification === 'Other' ? otherComments : justification,
              revokeList: lcompose(
                lmap<
                  IDeaNumUser,
                  {
                    deaInfoId: number;
                    serialNumber: string;
                    userProfileId: number;
                  }
                >(lpick(['deaInfoId', 'serialNumber', 'userProfileId'])),
                lfilter<IDeaNumUser>(({ key }) =>
                  selectedUserRoleKeys.includes(key)
                )
              )(userRoles),
            })
        )()
        : setApiCallStatus(a => ({ ...a, apiCallStatus: void 0 })),
    [
      justification,
      otherComments,
      selectedUserRoleKeys,
      user,
      userRoles,
      dispatchSaveRequest,
    ]
  );

  useConfirmSubmitRequest({
    apiCallStatus,
    dispatchRequest: dispatchConfirmSubmitRequest,
  });

  const [revokedUserRoleKeys, setRevokedUserRoleKeys] = useState<string[]>([]);

  const { operations } = useRoleOpContext();
  const disableOperationsHelpDsk = disableOperationsHelpDesk(operations);

  return (
    <Wrapper>
      <article>
        <h3>Search User Certificates</h3>
        <FilterWrapper>
          <ActionLabel id="email">Email:</ActionLabel>
          <StyledAutocomplete2
            value={email}
            labelledby="email"
            inputValue={emailInput}
            onInputChange={lcompose(
              ltap(
                lcompose(emap(lcompose(setEmail, always(null))), (a: string) =>
                  a === '' ? right(void 0) : left(void 0)
                )
              ),
              ltap(setEmailInput),
              ltrim
            )}
            options={activeEmailOptions}
            onChange={flow(
              setEmail,
              () => setDeaNumber(null),
              () => setSerialNumber(null)
            )}
            noOptionsText={
              emailInput.length < searchEmailsMinLength
                ? `Please enter minimum ${searchEmailsMinLength} leading characters to initiate search...`
                : 'No results...'
            }
          />

          <FilterLabel id="deaNum">DEA Registration Number:</FilterLabel>
          <input
            style={{
              padding: '0.5rem',
              border: '1px solid #ccc',
              borderRadius: '4px',
              outline: 'none',
              boxShadow: 'none',
            }}
            aria-labelledby="deaNum"
            value={deaNumber || ''}
            onBlur={e => setDeaNumber(ltrim(e.target.value))}
            onChange={e => {
              const trimmedValue = ltrim(e.target.value);
              setDeaNumber(trimmedValue);
              setEmail(null);
              setSerialNumber(null);
            }}
            type="text"
          />

          <FilterLabel id="serialNumber">
            Certificate Serial Number:
          </FilterLabel>

          <StyledAutocomplete2
            value={serialNumber}
            labelledby="serialNumber"
            inputValue={serialNumberInput}
            onInputChange={lcompose(
              ltap(
                lcompose(
                  emap(lcompose(setSerialNumber, always(null))),
                  (a: string) => (a === '' ? right(void 0) : left(void 0))
                )
              ),
              ltap(setSerialNumberInput),
              ltrim
            )}
            options={activeSerialNumberOptions}
            onChange={flow(
              setSerialNumber,
              () => setEmail(null),
              () => setDeaNumber(null)
            )}
            noOptionsText={
              serialNumberInput.length < searchEmailsMinLength
                ? `Please enter minimum ${searchEmailsMinLength} leading characters to initiate search...`
                : 'No results...'
            }
          />
        </FilterWrapper>
      </article>
      <SectionWrapper>
        <h3>
          Revoke List{' '}
          {!matchesGridBreakpoint && (
            <Checkbox
              color="primary"
              onClick={toggleUserRoleAll}
              checked={deaNumUsersAllSelected({
                deaNumUsers: excludeUserRoles(revokedUserRoleKeys)(filteredUserRoles),
                selectedUserRoleKeys,
              })}
            />
          )}
        </h3>

        {matchesGridBreakpoint ? (
          <UserRoles2d
            deaNumUsers={excludeUserRoles(revokedUserRoleKeys)(filteredUserRoles)}
            selectedUserRoleKeys={selectedUserRoleKeys}
            enableAll
            dispatchTeamRequest={toggleUserRole}
          />
        ) : (
          <UserRolesStack
            deaNumUsers={excludeUserRoles(revokedUserRoleKeys)(filteredUserRoles)}
            selectedUserRoleKeys={selectedUserRoleKeys}
            dispatchTeamRequest={toggleUserRole}
            properties={properties}
          />
        )}
      </SectionWrapper>
      <Actions>
        <Justification
          value={justification}
          options={diTab ? justificationOptions.DI_REVOKE : justificationOptions.REVOKE}
          onChange={setJustification}
          otherComments={otherComments}
          onChangeOtherComments={setOtherComments}
          tooltipValue={always(false)}
          toggleTooltip={noop}
          offTooltip={noop}
        />
      </Actions>

      <StatusSaveRequst apiCallStatus={apiCallStatus.apiCallStatus} />
      {apiCallStatus.apiCallStatus === true && (
        <article>
          <SuccessIcon />
          <span> {config.requestSubmitSuccess} </span>
        </article>
      )}

      <Button
        onClick={() =>
          setApiCallStatus(a => ({
            ...a,
            type: 'ConfirmSubmitRequest',
            apiCallStatus: null,
          }))
        }
        disabled={
          disableOperationsHelpDsk ||
          isEmpty(selectedUserRoleKeys) ||
          isEmpty(justification) ||
          apiCallStatus.apiCallStatus === null
        }
        variant="contained"
        color="primary">
        submit
      </Button>
    </Wrapper>
  );
};

const Wrapper = styled('article')(({ theme }) => ({
  '&>*+*': {
    marginTop: '3rem',
  },
}));

const FilterWrapper = styled('span')(({ theme }) => ({
  margin: '1.5rem',
  display: 'grid',
  gap: '0.5rem',

  '@media (min-width: 33.375em)': {
    display: 'grid',
    gridTemplateColumns: '200px  300px 200px 300px ',
    gap: '0.5rem',
    alignItems: 'center',
  },
}));

const SectionWrapper = styled('article')(({ theme }) => ({
  '&>*+*': {
    marginTop: '1rem',
  },
}));

const ActionLabel = styled('span')(({ theme }) => ({
  '@media (min-width: 33.375em)': {
    justifySelf: 'end',
  },
}));

const FilterLabel = styled('span')(({ theme }) => ({
  marginTop: '0.5rem',

  '@media (min-width: 33.375em)': {
    justifySelf: 'end',
    marginLeft: '5rem',
    marginTop: '0rem',
  },
}));

const Actions = styled('article')(({ theme }) => ({
  display: 'grid',
  gap: '0.5rem',
  alignItems: 'center',
  gridAutoRows: 'minmax(1rem,auto)',

  '@media (min-width: 33.375em)': {
    gridTemplateColumns: 'auto 200px auto auto auto 1fr',
  },
}));
